import React from "react";
import { ReactQuestionFactory } from "survey-react-ui";
import { QuestionNonValue, Serializer, ElementFactory } from "survey-core";

export class TipQuestionModel extends QuestionNonValue {
  static type = "tip";

  getType() {
    return TipQuestionModel.type;
  }

  get variant(): "tip" | "callout" {
    return this.getPropertyValue("variant", "tip");
  }

  set variant(val: "tip" | "callout") {
    this.setPropertyValue("variant", val);
  }

  get heading(): string {
    return this.getPropertyValue("heading");
  }

  set heading(val: string) {
    this.setPropertyValue("heading", val);
  }

  get content(): string {
    return this.getPropertyValue("content", "");
  }

  set content(val: string) {
    this.setPropertyValue("content", val);
  }

  get linkHref(): string {
    return this.getPropertyValue("linkHref");
  }

  set linkHref(val: string) {
    this.setPropertyValue("linkHref", val);
  }

  get linkText(): string {
    return this.getPropertyValue("linkText");
  }

  set linkText(val: string) {
    this.setPropertyValue("linkText", val);
  }

  public get name(): string {
    return this.getPropertyValue("name");
  }

  public set name(val: string) {
    this.setPropertyValue("name", val);
  }
}

interface TipQuestionComponentProps {
  question: TipQuestionModel;
}

export function TipQuestionComponent({ question }: TipQuestionComponentProps) {
  const variant = question.variant || "tip";
  const icon = variant === "tip" ? "💡" : "ℹ️";

  return (
    <div
      className={question.cssClasses?.root}
      style={{
        backgroundColor: "#f8f9fa",
        border: "1px solid #dee2e6",
        borderRadius: "4px",
        padding: "10px",
        marginBottom: "10px",
      }}
    >
      <div style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
        <span style={{ fontSize: "18px" }}>{icon}</span>
        <div style={{ flex: 1 }}>
          {question.heading && (
            <h4
              style={{
                margin: "0 0 4px 0",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              {question.heading}
            </h4>
          )}
          <p style={{ margin: "0", fontSize: "13px", lineHeight: "1.3" }}>
            {question.content}
          </p>
          {question.linkHref && question.linkText && (
            <a
              href={question.linkHref}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                marginTop: "4px",
                color: "#0066cc",
                textDecoration: "none",
              }}
            >
              {question.linkText} →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export function registerTipQuestion() {
  ElementFactory.Instance.registerElement(
    "tip",
    (name: string) => new TipQuestionModel(name),
  );

  Serializer.addClass(
    "tip",
    [
      {
        name: "variant",
        category: "general",
        displayName: "Variant",
        type: "dropdown",
        default: "tip",
        choices: [
          { value: "tip", text: "Tip (lightbulb)" },
          { value: "callout", text: "Callout (info circle)" },
        ],
      },
      {
        name: "heading",
        category: "general",
        displayName: "Heading",
        type: "string",
      },
      {
        name: "content",
        category: "general",
        displayName: "Content",
        type: "text",
        isRequired: true,
      },
      {
        name: "linkHref",
        category: "general",
        displayName: "Link URL",
        type: "string",
      },
      {
        name: "linkText",
        category: "general",
        displayName: "Link Text",
        type: "string",
        visibleIf(obj: TipQuestionModel) {
          return !!obj.linkHref;
        },
      },
    ],
    () => new TipQuestionModel(""),
    "nonvalue",
  );

  ReactQuestionFactory.Instance.registerQuestion(
    TipQuestionModel.type,
    (propsOrName: string | TipQuestionComponentProps) => {
      const props =
        typeof propsOrName === "string"
          ? { question: new TipQuestionModel(propsOrName) }
          : propsOrName;
      return React.createElement(TipQuestionComponent, props);
    },
  );
}

export function createTipQuestion(config: {
  name: string;
  variant?: "tip" | "callout";
  heading?: string;
  content: string;
  link?: {
    href: string;
    text: string;
  };
}) {
  return {
    type: "tip",
    name: config.name,
    variant: config.variant || "tip",
    heading: config.heading,
    content: config.content,
    linkHref: config.link?.href,
    linkText: config.link?.text,
  };
}
